import api from '../utils/api';

class UserService {
  // ───────────────────────── Admin User Management ─────────────────────────

  // List users (paged) - matches backend AdminUserController.listUsers
  async getAdminUsers(pageable = {}) {
    try {
      const response = await api.get('/users/admin', {
        params: pageable
      });
      // Backend returns Page<UserSummaryDto>
      return response.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Get user by ID - matches backend AdminUserController.getUser
  async getAdminUserById(userId) {
    try {
      const response = await api.get(`/users/admin/${userId}`);
      // Backend returns UserDto
      return response.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Enable user - matches backend AdminUserController.enable
  async enableUser(userId) {
    try {
      await api.post(`/users/admin/${userId}/enable`);
      // Backend returns void (200 OK)
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Disable user - matches backend AdminUserController.disable
  async disableUser(userId) {
    try {
      await api.post(`/users/admin/${userId}/disable`);
      // Backend returns void (200 OK)
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Add role to user - matches backend AdminUserController.addRole
  async addUserRole(userId, roleName) {
    try {
      await api.post(`/users/admin/${userId}/roles/${roleName}`);
      // Backend returns void (200 OK)
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Remove role from user - matches backend AdminUserController.removeRole
  async removeUserRole(userId, roleName) {
    try {
      await api.delete(`/users/admin/${userId}/roles/${roleName}`);
      // Backend returns void (204 No Content)
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Delete user - matches backend AdminUserController.delete
  async deleteUser(userId) {
    try {
      await api.delete(`/users/admin/${userId}`);
      // Backend returns void (204 No Content)
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // ───────────────────────── Admin Purchase Management ─────────────────────────

  // Get user video purchases (paged) - matches backend AdminUserController.getUserVideoPurchases
  async getUserVideoPurchases(pageable = {}) {
    try {
      const response = await api.get('/users/admin/video-purchases', {
        params: pageable
      });
      // Backend returns Page<UserVideoDto>
      return response.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Get user's last 10 purchases after influencer code usage - matches backend AdminUserController.getUserLast10PurchasesAfterInfluencerCodeUsage
  async getUserLast10PurchasesAfterInfluencerCodeUsage(userId) {
    try {
      const response = await api.get(`/users/admin/${userId}/purchases/influencer-latest`);
      // Backend returns List<PurchaseDto>
      return response.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Export users purchases to Excel - matches backend AdminUserController.exportUsersPurchasesToExcel
  async exportUsersPurchasesToExcel(months = null, year = null) {
    try {
      const params = {};
      if (months && months.length > 0) {
        // Convert array to comma-separated string to avoid invalid characters in URL
        params.months = months.join(',');
      }
      if (year) params.year = year;
      
      const response = await api.get('/users/admin/purchases/export', {
        responseType: 'arraybuffer',
        params: params
      });
      // Backend returns byte[] (Excel file)
      return response.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Get user footage purchase hours (paged) - matches backend AdminUserController.getUserFootagePurchaseHours
  async getUserFootagePurchaseHours(pageable = {}) {
    try {
      const response = await api.get('/users/admin/footage-hours', {
        params: pageable
      });
      // Backend returns Page<UserFootageView>
      return response.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Get my profile using /me endpoint
  async getMyProfile() {
    try {
      const response = await api.get('/users/me');
      return response.data.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Update my profile using /me endpoint
  async updateMyProfile(userData) {
    try {
      const response = await api.patch('/users/me', userData);
      return response.data.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Change my password using /me/password endpoint
  async changeMyPassword(newPassword) {
    try {
      await api.post('/users/me/password', { newPassword });
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Delete my account using /me endpoint
  async deleteMyAccount() {
    try {
      await api.delete('/users/me');
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Search user by username or email
  async searchUser(identifier) {
    try {
      const response = await api.get(`/users/search?identifier=${encodeURIComponent(identifier)}`);
      return response.data.data;
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Get purchased videos for a user (self-only)
  async getPurchasedVideosByUser(userId) {
    try {
      const response = await api.get(`/users/${userId}/purchased-videos`);
      
      // Handle backend error responses that return null
      if (response.data === null) {
        return []; // Return empty array if no videos found
      }
      
      return response.data.data || []; // ApiEnvelope wrapper or empty array
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Helper method to extract error message
  _extractErrorMessage(error) {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    
    if (error.message) {
      return error.message;
    }
    
    return 'An unexpected error occurred';
  }
}

const userService = new UserService();
export default userService;
